import {
  provideHttpClient,
  withInterceptors
} from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withRouterConfig
} from '@angular/router';
import { JwtModule } from "@auth0/angular-jwt";
import { AtlasMapboxModule } from '@cargo-signal/atlas-mapbox';
import { CsSharedModule } from '@cargo-signal/shared';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { authInterceptor } from './core/auth/interceptors/auth.interceptor';
import { unauthenticatedInterceptor } from './core/auth/interceptors/unauthenticated.interceptor';
import { AUTH_CONFIG } from './core/auth/tokens/auth-config.token';
import { dataSourcesConfig } from './data-sources/data-sources.config';
import { GlobalErrorHandler } from './shared/services/global-error-handler/global-error-handler';
import { sharedConfig } from './shared/shared.config';
import { shipmentsConfig } from './shipments/shipments.config';
import {
  CustomRouterStateSerializer,
  reducers
} from './store/conventional';
import { effects } from './store/conventional/effects';
import { provideSignalStore } from './store/signal';

export function jwtTokenGetter() {
  return localStorage.getItem('authToken');
}

export const providers: ApplicationConfig["providers"] = [
  provideHttpClient(withInterceptors([...environment.interceptors, authInterceptor, unauthenticatedInterceptor])),
  provideRouter(routes, withRouterConfig({onSameUrlNavigation: 'reload'})),
  provideAnimations(),
  provideStore(reducers, {
    runtimeChecks: {
      strictStateImmutability: true,
      strictActionImmutability: true,
    },
  }),
  provideEffects(effects),
  provideRouterStore({ serializer: CustomRouterStateSerializer }),
  provideSignalStore(),
  environment.providers,
  {
    provide: AUTH_CONFIG,
    useValue: {
      clientID: environment.auth.clientID,
      domain: environment.auth.domain,
      scope: environment.auth.scope,
      audience: environment.auth.audience,
      redirect: environment.auth.redirect,
      appUrlPrefix: environment.appUrlPrefix
    }
  },
  importProvidersFrom(
    JwtModule.forRoot({
      config: {
        tokenGetter: jwtTokenGetter,
        allowedDomains: ['localhost:4200', 'cargosignal.com', 'cargosignal.dev'],
        skipWhenExpired: true
      },
    }),
    CsSharedModule.forRoot({
      clientID: environment.auth.clientID,
      domain: environment.auth.domain,
      scope: environment.auth.scope,
      audience: environment.auth.audience,
      redirect: environment.auth.redirect,
      appUrlPrefix: environment.appUrlPrefix
    }),
    AtlasMapboxModule.forRoot({
      mapboxApiKey: environment.mapboxApiKey,
      mapboxChinaApiKey: environment.mapboxChinaApiKey
    }),
    NotificationModule,
    DialogModule,
  ),
  sharedConfig.providers,
  dataSourcesConfig.providers,
  shipmentsConfig.providers,
  { provide: ErrorHandler, useClass: GlobalErrorHandler },
]

export const appConfig: ApplicationConfig = {
  providers,
};
